import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Stack, Text, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import { getOrganizationDisplayName } from '@/common/organizations-flyout/src/utils';
import { useFastshiftSelector } from '@/state';

import { useOrganizationsListAnalytics } from './hooks/use-organizations-list-analytics';
import { messages } from './messages';
import { OrganizationCard } from './organization-card/organization-card';
import {
	OrganizationsPageEmptyState,
	OrganizationsPageLoadingState,
} from './organizations-list-states';

const styles = cssMap({
	OrganizationGrid: {
		gap: token('space.200'),
		gridTemplateColumns: '1fr 1fr 1fr',
	},
});

export const FastshiftOrganizationsListPage = () => {
	const { formatMessage } = useIntl();
	const { sendOrganizationCardClickUIEvent } = useOrganizationsListAnalytics();
	const {
		data: organizations,
		pending,
		error,
	} = useFastshiftSelector((state) => state.organizations);

	if (pending) {
		return <OrganizationsPageLoadingState />;
	}

	const isEmpty = organizations === null || organizations.length === 0;

	return (
		<Stack space="space.300">
			<Stack space="space.150">
				<Heading size="large">{formatMessage(messages.pageHeading)}</Heading>
				{isEmpty || error ? null : (
					<Text color="color.text.subtle">{formatMessage(messages.pageDescription)}</Text>
				)}
			</Stack>
			{error ? (
				<GenericErrorDisplay customMessage={formatMessage(messages.errorDescription)} />
			) : isEmpty ? (
				<OrganizationsPageEmptyState />
			) : (
				<Grid xcss={styles.OrganizationGrid}>
					{organizations?.map((organization) => (
						<OrganizationCard
							key={organization.orgId}
							organizationId={organization.orgId}
							onClick={() => sendOrganizationCardClickUIEvent(organization.orgId)}
							organizationName={getOrganizationDisplayName(organization, { formatMessage })}
						/>
					))}
				</Grid>
			)}
		</Stack>
	);
};
